import mixpanel from 'mixpanel-browser';
import { isMixpanelEnabled, registerEnvironment } from '@/@configs/mixpanel';
import type {
  AnalyticsEvent,
  AnalyticsEventProperties,
} from '@/constants/analytics';
import type { Plan } from '@/models/bootstrap/plan';

interface AnalyticsUser {
  id: number | string;
  name?: string;
  email?: string;
  companyId?: number | string;
  plan?: Plan;
  experiments: string[];
  permissions: string[];
}

interface TrackOptions {
  beforeUnload?: boolean;
}

export const track = <Event extends AnalyticsEvent>(
  event: Event,
  properties: AnalyticsEventProperties[Event],
  { beforeUnload = false }: TrackOptions = {}
) => {
  if (!isMixpanelEnabled()) return;

  if (beforeUnload) {
    mixpanel.track(event, properties, {
      transport: 'sendBeacon',
      send_immediately: true,
    });
    return;
  }

  mixpanel.track(event, properties);
};

export const identify = ({
  id,
  name,
  email,
  companyId,
  plan,
  experiments,
  permissions,
}: AnalyticsUser) => {
  if (!isMixpanelEnabled()) return;

  const properties = {
    company_id: companyId,
    experiments,
    permissions,
    plan_features: plan?.features.map((feature) => feature.name) ?? 'N/A',
    plan_name: plan?.name ?? 'N/A',
    plan_code: plan?.code ?? 'N/A',
  };

  mixpanel.identify(String(id));
  mixpanel.register(properties);
  mixpanel.people.set({ $name: name, $email: email, ...properties });
};

export const reset = () => {
  if (!isMixpanelEnabled()) return;

  mixpanel.reset();
  registerEnvironment();
};
